import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import styles from '../ShutdownRitual.module.css';

interface ReflectionStepProps {
  reflection: string;
  onReflectionChange: (value: string) => void;
}

const PROMPTS = [
  'What went well today?',
  'What could have been better?',
  'What am I grateful for?',
  'What did I learn?',
  'How do I feel right now?',
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
};

export function ReflectionStep({ reflection, onReflectionChange }: ReflectionStepProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handlePromptClick = (prompt: string) => {
    const prefix = reflection.trim() ? `${reflection.trim()}\n\n` : '';
    onReflectionChange(`${prefix}${prompt}\n`);
    textareaRef.current?.focus();
  };

  return (
    <motion.div
      className={styles.stepContent}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.h2 className={styles.stepTitle} variants={itemVariants}>
        Daily Reflection
      </motion.h2>
      <motion.p className={styles.stepSubtitle} variants={itemVariants}>
        Take a moment to reflect on your day. This is optional but can help you grow and improve.
      </motion.p>

      {/* Prompts */}
      <motion.div className={styles.reflectionPrompts} variants={itemVariants}>
        {PROMPTS.map((prompt) => (
          <motion.button
            key={prompt}
            className={styles.promptBtn}
            onClick={() => handlePromptClick(prompt)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {prompt}
          </motion.button>
        ))}
      </motion.div>

      {/* Textarea */}
      <motion.div variants={itemVariants}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          placeholder="Write your thoughts here... or leave blank to skip."
          value={reflection}
          onChange={(e) => onReflectionChange(e.target.value)}
          aria-label="Daily reflection"
        />
      </motion.div>

      {/* Character count */}
      <motion.p
        style={{ fontSize: '11px', color: '#666', textAlign: 'right', marginTop: '8px' }}
        variants={itemVariants}
      >
        {reflection.length} characters
      </motion.p>
    </motion.div>
  );
}

export default ReflectionStep;
